import { useState, useEffect, useRef } from 'react';
import { useDebouncedValue } from '@mantine/hooks';

interface UseDebounceSearchParams<T> {
  searchFunction: (query: string) => Promise<T[]>;
  debounceDelay?: number;
  minQueryLength?: number;
  onError?: (error: unknown) => void;
}

interface UseDebounceSearchReturn<T> {
  query: string;
  setQuery: (query: string) => void;
  results: T[];
  loading: boolean;
}

export const useDebounceSearch = <T>({
  searchFunction,
  debounceDelay = 300,
  minQueryLength = 2,
  onError,
}: UseDebounceSearchParams<T>): UseDebounceSearchReturn<T> => {
  const [query, setQuery] = useState('');
  const [debounced] = useDebouncedValue(query, debounceDelay);
  const [results, setResults] = useState<T[]>([]);
  const [loading, setLoading] = useState(false);
  
  // Use refs to store the latest values without causing re-renders
  const searchFunctionRef = useRef(searchFunction);
  const onErrorRef = useRef(onError);
  
  // Update refs when props change (no re-render trigger)
  searchFunctionRef.current = searchFunction;
  onErrorRef.current = onError;

  useEffect(() => {
    const performSearch = async (q: string) => {
      const trimmed = q.trim();
      if (trimmed.length < minQueryLength) {
        setResults([]);
        return;
      }

      setLoading(true);
      try {
        const searchResults = await searchFunctionRef.current(trimmed);
        setResults(searchResults);
      } catch (error) {
        setResults([]);
        onErrorRef.current?.(error);
      } finally {
        setLoading(false);
      }
    };

    performSearch(debounced);
  }, [debounced, minQueryLength]); // Only depend on values that should trigger re-search

  return { query, setQuery, results, loading };
};
